import React from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Stack,
  Chip,
  LinearProgress,
  Divider,
} from '@mui/material';
import { LineChart, BarChart, PieChart } from '@mui/x-charts';
import { Trade } from '../../contexts/TradingJournalContext';
import { format, startOfMonth, eachMonthOfInterval, subMonths } from 'date-fns';

interface TradingAnalyticsProps {
  trades: Trade[];
}

const TradingAnalytics: React.FC<TradingAnalyticsProps> = ({ trades }) => {
  const closedTrades = trades.filter(trade => trade.status === 'closed' && trade.pnl !== undefined);
  
  // Calculate cumulative P&L for equity curve
  const equityCurve = closedTrades.reduce((acc, trade, index) => {
    const prevValue = index > 0 ? acc[index - 1].cumulative : 0;
    acc.push({
      date: new Date(trade.exitTime || trade.entryTime),
      pnl: trade.pnl || 0,
      cumulative: prevValue + (trade.pnl || 0),
      trade: index + 1,
    });
    return acc;
  }, [] as Array<{ date: Date; pnl: number; cumulative: number; trade: number }>);

  // Monthly performance
  const last6Months = eachMonthOfInterval({
    start: subMonths(new Date(), 5),
    end: new Date(),
  });

  const monthlyData = last6Months.map((month: Date) => {
    const monthTrades = closedTrades.filter(trade => {
      const tradeMonth = startOfMonth(new Date(trade.exitTime || trade.entryTime));
      return tradeMonth.getTime() === month.getTime();
    });
    
    const totalPnL = monthTrades.reduce((sum, trade) => sum + (trade.pnl || 0), 0);
    const wins = monthTrades.filter(trade => (trade.pnl || 0) > 0).length;
    const total = monthTrades.length;
    
    return {
      month: format(month, 'MMM yyyy'),
      pnl: totalPnL,
      trades: total,
      winRate: total > 0 ? (wins / total) * 100 : 0,
    };
  });

  // Symbol distribution
  const symbolStats = closedTrades.reduce((acc, trade) => {
    const symbol = trade.symbol;
    if (!acc[symbol]) {
      acc[symbol] = { count: 0, pnl: 0 };
    }
    acc[symbol].count += 1;
    acc[symbol].pnl += trade.pnl || 0;
    return acc;
  }, {} as Record<string, { count: number; pnl: number }>);

  const symbolChartData = Object.entries(symbolStats)
    .sort(([,a], [,b]) => b.count - a.count)
    .slice(0, 8)
    .map(([symbol, stats]) => ({
      id: symbol,
      value: stats.count,
      label: symbol,
    }));

  // Strategy performance
  const strategyStats = closedTrades.reduce((acc, trade) => {
    const strategy = trade.strategy || 'Unknown';
    if (!acc[strategy]) {
      acc[strategy] = { count: 0, pnl: 0, wins: 0 };
    }
    acc[strategy].count += 1;
    acc[strategy].pnl += trade.pnl || 0;
    if ((trade.pnl || 0) > 0) acc[strategy].wins += 1;
    return acc;
  }, {} as Record<string, { count: number; pnl: number; wins: number }>);

  // Time-based analysis
  const hourlyStats = closedTrades.reduce((acc, trade) => {
    const hour = new Date(trade.entryTime).getHours();
    if (!acc[hour]) {
      acc[hour] = { count: 0, pnl: 0, wins: 0 };
    }
    acc[hour].count += 1;
    acc[hour].pnl += trade.pnl || 0;
    if ((trade.pnl || 0) > 0) acc[hour].wins += 1;
    return acc;
  }, {} as Record<number, { count: number; pnl: number; wins: number }>);

  const hourlyData = Array.from({ length: 24 }, (_, hour) => ({
    hour: `${hour}:00`,
    trades: hourlyStats[hour]?.count || 0,
    pnl: hourlyStats[hour]?.pnl || 0,
    winRate: hourlyStats[hour] ? (hourlyStats[hour].wins / hourlyStats[hour].count) * 100 : 0,
  }));

  // Drawdown calculation
  const drawdownData = equityCurve.map((point, index) => {
    const peak = equityCurve.slice(0, index + 1).reduce((max, p) => Math.max(max, p.cumulative), 0);
    const drawdown = peak - point.cumulative;
    const drawdownPercent = peak > 0 ? (drawdown / peak) * 100 : 0;
    return {
      trade: point.trade,
      drawdown: drawdownPercent,
      equity: point.cumulative,
    };
  });

  const maxDrawdown = Math.max(...drawdownData.map(d => d.drawdown));

  if (closedTrades.length === 0) {
    return (
      <Box textAlign="center" py={4}>
        <Typography variant="h6" color="text.secondary">
          No closed trades available for analytics
        </Typography>
        <Typography variant="body2" color="text.secondary" mt={1}>
          Complete some trades to see detailed analytics
        </Typography>
      </Box>
    );
  }

  return (
    <Stack spacing={3}>
      {/* Equity Curve */}
      <Card sx={{ borderRadius: 2, boxShadow: 2 }}>
        <CardContent>
          <Typography variant="h6" fontWeight="bold" gutterBottom>
            Equity Curve
          </Typography>
          <Box sx={{ height: 300, width: '100%' }}>
            <LineChart
              dataset={equityCurve}
              xAxis={[{ 
                scaleType: 'linear', 
                dataKey: 'trade',
                label: 'Trade Number'
              }]}
              series={[
                { 
                  dataKey: 'cumulative', 
                  label: 'Cumulative P&L ($)',
                  color: '#1976d2',
                  curve: 'linear'
                },
              ]}
              width={undefined}
              height={250}
            />
          </Box>
        </CardContent>
      </Card>

      {/* Monthly Performance */}
      <Card sx={{ borderRadius: 2, boxShadow: 2 }}>
        <CardContent>
          <Typography variant="h6" fontWeight="bold" gutterBottom>
            Monthly Performance (Last 6 Months)
          </Typography>
          <Box sx={{ height: 300, width: '100%' }}>
            <BarChart
              dataset={monthlyData}
              xAxis={[{ scaleType: 'band', dataKey: 'month' }]}
              series={[
                { 
                  dataKey: 'pnl', 
                  label: 'P&L ($)',
                  color: '#4caf50',
                },
              ]}
              width={undefined}
              height={250}
            />
          </Box>
        </CardContent>
      </Card>

      {/* Side by side charts */}
      <Box display="flex" gap={3} flexDirection={{ xs: 'column', md: 'row' }}>
        {/* Symbol Distribution */}
        <Card sx={{ borderRadius: 2, boxShadow: 2, flex: 1 }}>
          <CardContent>
            <Typography variant="h6" fontWeight="bold" gutterBottom>
              Most Traded Symbols
            </Typography>
            <Box sx={{ height: 300, width: '100%' }}>
              <PieChart
                series={[
                  {
                    data: symbolChartData,
                  },
                ]}
                width={undefined}
                height={250}
                slotProps={{
                  legend: {
                    position: { vertical: 'bottom', horizontal: 'center' },
                  },
                }}
              />
            </Box>
          </CardContent>
        </Card>

        {/* Trading Hours Analysis */}
        <Card sx={{ borderRadius: 2, boxShadow: 2, flex: 1 }}>
          <CardContent>
            <Typography variant="h6" fontWeight="bold" gutterBottom>
              Trading Hours Performance
            </Typography>
            <Box sx={{ height: 300, width: '100%' }}>
              <BarChart
                dataset={hourlyData.filter(h => h.trades > 0)}
                xAxis={[{ scaleType: 'band', dataKey: 'hour' }]}
                series={[
                  { 
                    dataKey: 'pnl', 
                    label: 'P&L ($)',
                    color: '#ff9800',
                  },
                ]}
                width={undefined}
                height={250}
              />
            </Box>
          </CardContent>
        </Card>
      </Box>

      {/* Strategy Performance */}
      <Card sx={{ borderRadius: 2, boxShadow: 2 }}>
        <CardContent>
          <Typography variant="h6" fontWeight="bold" gutterBottom>
            Strategy Performance Analysis
          </Typography>
          
          <Stack spacing={2}>
            {Object.entries(strategyStats)
              .sort(([,a], [,b]) => b.pnl - a.pnl)
              .map(([strategy, stats]) => (
                <Box key={strategy}>
                  <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                    <Typography variant="body1" fontWeight="medium">
                      {strategy}
                    </Typography>
                    <Box display="flex" alignItems="center" gap={2}>
                      <Chip
                        label={`${stats.count} trades`}
                        size="small"
                        variant="outlined"
                      />
                      <Typography
                        variant="body2"
                        fontWeight="bold"
                        color={stats.pnl >= 0 ? 'success.main' : 'error.main'}
                      >
                        {stats.pnl >= 0 ? '+' : ''}${stats.pnl.toFixed(2)}
                      </Typography>
                    </Box>
                  </Box>
                  
                  <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Typography variant="caption" color="text.secondary">
                      Win Rate: {((stats.wins / stats.count) * 100).toFixed(1)}%
                    </Typography>
                    <LinearProgress
                      variant="determinate"
                      value={(stats.wins / stats.count) * 100}
                      sx={{ width: 120 }}
                      color={stats.wins / stats.count >= 0.5 ? 'success' : 'error'}
                    />
                  </Box>
                  
                  {strategy !== Object.keys(strategyStats)[Object.keys(strategyStats).length - 1] && (
                    <Divider sx={{ mt: 2 }} />
                  )}
                </Box>
              ))}
          </Stack>
        </CardContent>
      </Card>

      {/* Risk Metrics */}
      <Card sx={{ borderRadius: 2, boxShadow: 2 }}>
        <CardContent>
          <Typography variant="h6" fontWeight="bold" gutterBottom>
            Risk Metrics
          </Typography>
          
          <Box display="flex" flexWrap="wrap" gap={3}>
            <Box>
              <Typography variant="h4" fontWeight="bold" color="error.main">
                {maxDrawdown.toFixed(2)}%
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Maximum Drawdown
              </Typography>
            </Box>
            
            <Box>
              <Typography variant="h4" fontWeight="bold" color="info.main">
                {(equityCurve[equityCurve.length - 1]?.cumulative / closedTrades.length || 0).toFixed(2)}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Avg P&L per Trade
              </Typography>
            </Box>
            
            <Box>
              <Typography variant="h4" fontWeight="bold" color="primary.main">
                {closedTrades.length}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Total Completed Trades
              </Typography>
            </Box>
          </Box>
        </CardContent>
      </Card>
    </Stack>
  );
};

export default TradingAnalytics; 